import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ReportJobsService } from '../../../services/report-jobs.service';
import { ReportHubService } from '../../../services/report-hub.service';
import { ToastService } from '../../../services/toast.service';
import { ProjectService } from '../../../services/project.service';
import { ParticipantService } from '../../../services/participant.service';
import { CompanyService } from '../../../services/company.service';

interface DropdownOption {
  label: string;
  value: string | number;
}

@Component({
  selector: 'app-portfolio-reports',
  templateUrl: './portfolio-reports.component.html',
  styleUrls: ['./portfolio-reports.component.scss']
})
export class PortfolioReportsComponent implements OnInit, OnDestroy {
  loading = false;
  reportForm!: FormGroup;

  startDate: Date | null = null;
  endDate: Date | null = null;

  projectsDropdown: DropdownOption[] = [];
  participantsDropdown: DropdownOption[] = [];
  companiesDropdown: DropdownOption[] = [];

  projects: any[] = [];
  participants: any[] = [];
  companies: any[] = [];

  private reportHubSub!: Subscription;
  private pendingJobId: string | null = null;

  constructor(
    private fb: FormBuilder,
    private _reportJobsService: ReportJobsService,
    private _reportHubService: ReportHubService,
    private _toastService: ToastService,
    private _projectsService: ProjectService,
    private _participantsService: ParticipantService,
    private _companiesService: CompanyService
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.loadDropdowns();

    this.reportHubSub = this._reportHubService.reportStatusChanged$.subscribe(notification => {
      if (notification.jobId !== this.pendingJobId) return;

      if (notification.status === 'Completed') {
        this.loading = false;
        this._toastService.showToast('success', 'Reporte de cartera generado exitosamente. Descargando...');
        this._reportJobsService.downloadReport(notification.jobId, notification.fileName || undefined);
        this.pendingJobId = null;
      } else if (notification.status === 'Failed') {
        this.loading = false;
        this._toastService.showToast('error', `Error al generar el reporte: ${notification.errorMessage || 'Desconocido'}`);
        this.pendingJobId = null;
      } else if (notification.status === 'Processing') {
        this._toastService.showToast('info', 'Procesando reporte de cartera...');
      }
    });
  }

  ngOnDestroy(): void {
    this.reportHubSub?.unsubscribe();
  }

  private initForm(): void {
    this.reportForm = this.fb.group({
      projectId: [null],
      participantId: [null],
      companyId: [null]
    });
  }

  // Helper para extraer datos de estructura paginada o array directo
  private extractData(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (response?.data) {
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    }
    return [];
  }

  private loadDropdowns(): void {
    this._projectsService.getAll().subscribe((res: any) => {
      this.projects = this.extractData(res);
      this.projectsDropdown = this.projects.map((p: any) => ({
        label: `${p.projectId} - ${p.name}`,
        value: p.id
      }));
    });

    this._participantsService.getAll().subscribe((res: any) => {
      this.participants = this.extractData(res);
      this.participantsDropdown = this.participants.map((p: any) => ({
        label: `${p.firstName} ${p.lastName} - ${p.typeDocument || 'N/A'}: ${p.documentId || 'N/A'}`,
        value: p.id
      }));
    });

    this._companiesService.getAll().subscribe((res: any) => {
      this.companies = this.extractData(res);
      this.companiesDropdown = this.companies.map((c: any) => ({
        label: `${c.name} - ${c.typeDocument || 'N/A'}: ${c.documentId || 'N/A'}`,
        value: c.id
      }));
    });
  }

  // 🔍 Búsqueda server-side para proyectos
  onSearchProjects(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      this._projectsService.getAll().subscribe((res: any) => {
        this.projects = this.extractData(res);
        this.projectsDropdown = this.projects.map((p: any) => ({
          label: `${p.projectId} - ${p.name}`,
          value: p.id
        }));
      });
      return;
    }
    this._projectsService.getAllFiltered(query).subscribe((res: any) => {
      this.projects = this.extractData(res);
      this.projectsDropdown = this.projects.map((p: any) => ({
        label: `${p.projectId} - ${p.name}`,
        value: p.id
      }));
    });
  }

  // 🔍 Búsqueda server-side para participantes
  onSearchParticipants(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      this._participantsService.getAll().subscribe((res: any) => {
        this.participants = this.extractData(res);
        this.participantsDropdown = this.participants.map((p: any) => ({
          label: `${p.firstName} ${p.lastName} - ${p.typeDocument || 'N/A'}: ${p.documentId || 'N/A'}`,
          value: p.id
        }));
      });
      return;
    }
    this._participantsService.getAllFiltered(query).subscribe((res: any) => {
      this.participants = this.extractData(res);
      this.participantsDropdown = this.participants.map((p: any) => ({
        label: `${p.firstName} ${p.lastName} - ${p.typeDocument || 'N/A'}: ${p.documentId || 'N/A'}`,
        value: p.id
      }));
    });
  }

  // 🔍 Búsqueda server-side para empresas
  onSearchCompanies(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      this._companiesService.getAll().subscribe((res: any) => {
        this.companies = this.extractData(res);
        this.companiesDropdown = this.companies.map((c: any) => ({
          label: `${c.name} - ${c.typeDocument || 'N/A'}: ${c.documentId || 'N/A'}`,
          value: c.id
        }));
      });
      return;
    }
    this._companiesService.getAllFiltered(query).subscribe((res: any) => {
      this.companies = this.extractData(res);
      this.companiesDropdown = this.companies.map((c: any) => ({
        label: `${c.name} - ${c.typeDocument || 'N/A'}: ${c.documentId || 'N/A'}`,
        value: c.id
      }));
    });
  }

  downloadExcelReport(): void {
    if (this.startDate && this.endDate && this.startDate > this.endDate) {
      this._toastService.showToast('warning', 'La fecha de inicio no puede ser posterior a la fecha de fin');
      return;
    }

    this.loading = true;
    const filters = {
      startDate: this.startDate,
      endDate: this.endDate,
      projectId: this.reportForm.get('projectId')?.value ?? null,
      participantId: this.reportForm.get('participantId')?.value ?? null,
      companyId: this.reportForm.get('companyId')?.value ?? null
    };

    this._reportJobsService.queuePortfolioReport(filters).subscribe({
      next: (response) => {
        this.pendingJobId = response.jobId;
        this._toastService.showToast('info', 'Generando reporte de cartera en segundo plano...');
      },
      error: (error: any) => {
        console.error('Error al solicitar reporte de cartera:', error);
        this.loading = false;
        this._toastService.showToast('error', 'Hubo un error al solicitar el reporte de cartera.');
      }
    });
  }

  get selectedProjectName(): string {
    if (!this.reportForm.get('projectId')?.value) return 'Todos los proyectos';
    const project = this.projects.find(p => p.id === this.reportForm.get('projectId')?.value);
    return project ? `${project.projectId} - ${project.name}` : 'Todos';
  }

  get selectedParticipantName(): string {
    if (!this.reportForm.get('participantId')?.value) return 'Todos los participantes';
    const participant = this.participants.find(p => p.id === this.reportForm.get('participantId')?.value);
    return participant ? `${participant.firstName} ${participant.lastName} - ${participant.typeDocument || 'N/A'}: ${participant.documentId || 'N/A'}` : 'Todos';
  }

  get selectedCompanyName(): string {
    if (!this.reportForm.get('companyId')?.value) return 'Todas las empresas';
    const company = this.companies.find(c => c.id === this.reportForm.get('companyId')?.value);
    return company ? `${company.name} - ${company.typeDocument || 'N/A'}: ${company.documentId || 'N/A'}` : 'Todas';
  }
}
